import { useEffect, useMemo, useState } from 'react';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { logInfo } from 'setup/logger/logInfo';
import { isChannelRequestError } from 'shared/channels';
import { isEntityNotDownloadedAnswer } from 'shared/channels/entity';
import type { Identificable } from 'shared/types/commonView';
import { subscribeToEntityChanges } from './subscribeToEntityChanges';
import type { EntityCollectionClient } from './types';

/** The parts of an entity client a details view needs. */
export type EntityByIdClient<TModel> = Pick<EntityCollectionClient<TModel>, 'onEvent' | 'readCollection'> & {
  getOneById: (id: string) => Promise<TModel>;
};

interface UseEntityByIdParams<TModel> {
  id: string | undefined;
  client: EntityByIdClient<TModel>;
  /** Items of the entity's store; preferred, as they follow every change. */
  items: TModel[];
  /** For logs, e.g. `companies`. */
  entityName: string;
}

/** One entity read by id: `loading` until the store or the worker has it, or the read failed. */
export interface EntityByIdRead<TModel> {
  item: TModel | undefined;
  loading: boolean;
  /**
   * The read did not tell whether the entity exists: the worker did not
   * answer (a timeout, a stopped worker) or its entity is not downloaded
   * (offline). Without it, a missing `item` means the worker found none.
   */
  readFailed?: boolean;
}

interface ReadItem<TModel> {
  id: string;
  item?: TModel;
  settled: boolean;
  /** Settled without an answer of the worker. */
  failed?: boolean;
}

/**
 * One entity for a details view: from the store when it holds it, otherwise
 * read by id from the worker (archived items too), and read again after
 * changes of the entity and when the worker's config is ready again. `loading` lasts until either has it or the read failed.
 */
export function useEntityById<TModel extends Identificable>({
  id,
  client,
  items,
  entityName,
}: UseEntityByIdParams<TModel>): EntityByIdRead<TModel> {
  const [read, setRead] = useState<ReadItem<TModel>>();

  useEffect(
    function readItemById() {
      if (!id) {
        return undefined;
      }
      let isCurrent = true;
      const readItem = (): void => {
        client
          .getOneById(id)
          .then((item) => isCurrent && setRead({ id, item, settled: true }))
          .catch((error: unknown) => {
            logInfo('entity-by-id-load-failed', { entity: entityName, id, error });
            if (isCurrent) {
              // The worker answers a missing item with an error; no answer, or an entity not downloaded, leaves it unknown.
              const failed = !isChannelRequestError(error) || isEntityNotDownloadedAnswer(error);
              setRead((previous) => ({
                id,
                item: previous && areObjectIdsEqual(previous.id, id) ? previous.item : undefined,
                settled: true,
                failed,
              }));
            }
          });
      };

      setRead({ id, settled: false });
      readItem();
      const stopFollowingChanges = subscribeToEntityChanges(client, readItem);
      return (): void => {
        isCurrent = false;
        stopFollowingChanges();
      };
    },
    [client, entityName, id]
  );

  return useMemo(() => {
    const readOfId = read && areObjectIdsEqual(read.id, id) ? read : undefined;
    const item = items.find((candidate) => areObjectIdsEqual(candidate._id, id)) ?? readOfId?.item;
    return { item, loading: !item && Boolean(id) && !readOfId?.settled, readFailed: !item && Boolean(readOfId?.failed) };
  }, [id, items, read]);
}
